import { ThemeProvider } from "styled-components";
import { useState, useEffect } from "react";
import { darkTheme, lightTheme } from './utils/Themes.js';
import Navbar from "./components/Navbar";
import './App.css';
import { BrowserRouter as Router } from 'react-router-dom';
import HeroSection from "./components/HeroSection";
import Skills from "./components/Skills";
import Projects from "./components/Projects";
import Footer from "./components/Footer";
import Experience from "./components/Experience";
import Education from "./components/Education";
import ProjectDetails from "./components/ProjectDetails";
import ProjectCaseStudy from "./components/ProjectCaseStudy";
import styled from "styled-components";
import ReactGA from 'react-ga4';
import AchievementSection from "./components/Achievement/index.js";
import CertificationSection from './components/Certification/index.js';
import FreelancingSection from './components/Freelancing/index.js';
import ContactModal from './components/ContactModal/index.js';
import LocationModal from './components/LocationModal/index.js';
import LoadingScreen from './components/LoadingScreen/index.js';
// Uncomment to re-enable landing video intro
// import VideoIntro from './components/VideoIntro';
import LinkedInFeatured from './components/LinkedInFeatured/index.js';
import BackToTop from './components/BackToTop';
import StickyDotNav from './components/StickyDotNav';
import Testimonials from './components/Testimonials';
import Blog from './components/Blog';
import CustomCursor from './components/CustomCursor';
import PageBorderTrail from './components/PageBorderTrail';
import ChatWidget from './components/ChatWidget';
import AdminDashboard from './components/AdminDashboard';
import { Routes, Route } from 'react-router-dom';
import GlobalTopoBackground from './components/GlobalTopoBackground';
import FloatingContactButton from './components/FloatingContactButton';

// Solid background behind the topo canvas
const BgBase = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.bg};
  z-index: -1;
`;

const Body = styled.div`
  background: transparent;
  width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const Wrapper = styled.div`
  background: linear-gradient(38.73deg, rgba(204, 0, 187, 0.15) 0%, rgba(201, 32, 184, 0) 50%), linear-gradient(141.27deg, rgba(0, 70, 209, 0) 50%, rgba(0, 70, 209, 0.15) 100%);
  width: 100%;
  clip-path: polygon(0 0, 100% 0, 100% 100%,30% 98%, 0 100%);
`;

// CTA Banner — a persistent mid-page call-to-action
const CTABannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  gap: 16px;
  background: linear-gradient(135deg, rgba(133, 76, 230, 0.08) 0%, rgba(0, 70, 209, 0.08) 100%);
  border-top: 1px solid rgba(133, 76, 230, 0.15);
  border-bottom: 1px solid rgba(133, 76, 230, 0.15);

  @media (max-width: 768px) {
    padding: 40px 16px;
  }
`;

const CTATitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CTASubtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text_secondary};
  max-width: 500px;
  line-height: 1.6;
  margin: 0;
`;

const CTAButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  margin-top: 8px;
`;

const CTAPrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  color: white;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(133, 76, 230, 0.4);
  }
`;

const CTASecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: transparent;
  color: hsla(271, 100%, 50%, 1);
  border: 2px solid hsla(271, 100%, 50%, 1);
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: hsla(271, 100%, 50%, 0.12);
    transform: translateY(-2px);
    box-shadow: 0 0 20px hsla(271, 100%, 50%, 0.3);
  }
`;

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [openModal, setOpenModal] = useState({ state: false, project: null });
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Uncomment to re-enable the landing video intro
  // const [showIntro, setShowIntro] = useState(false);

  // GA initialization
  useEffect(() => {
    ReactGA.initialize('G-XM4PRCRRWK');
  }, []);

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("darkMode");
    if (savedTheme !== null) {
      setDarkMode(JSON.parse(savedTheme));
    }
  }, []);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/",
      title: "Home"
    });
  }, []);

  // Loading screen timer - disabled
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const toggleTheme = () => setDarkMode((prev) => !prev);

  // VIDEO INTRO DISABLED — uncomment below and set showIntro default to true to re-enable
  // if (showIntro) {
  //   return <VideoIntro onComplete={() => setShowIntro(false)} />;
  // }

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
              {/* Layer 1: solid bg color (behind canvas) */}
              <BgBase />
              {/* Layer 2: mouse-reactive topo lines */}
              <GlobalTopoBackground />
              <PageBorderTrail />
              <CustomCursor />
              <LoadingScreen isVisible={isLoading} />
              {!isLoading && (
                <Router>
                  <Routes>
                    <Route path="/private-dashboard" element={<AdminDashboard />} />
                    <Route path="/projects/:slug" element={
                      <ProjectCaseStudy />
                    } />
                    <Route path="/" element={
                      <>
                        <Navbar
                          toggleTheme={toggleTheme}
                          darkMode={darkMode}
                          onContactClick={() => setContactModalOpen(true)}
                          onLocationClick={() => setLocationModalOpen(true)}
                        />
                        <Body>
                          <HeroSection />
                          <Wrapper>
                            <Experience />
                          </Wrapper>
                          <Projects openModal={openModal} setOpenModal={setOpenModal} />
                          <Wrapper>
                            <Skills />
                            <Education />
                          </Wrapper>
                          <AchievementSection />
                          <CertificationSection />
                          <FreelancingSection />
                          <LinkedInFeatured />
                          <Blog />
                          <CTABannerContainer>
                            <CTATitle>Interested in working together?</CTATitle>
                            <CTASubtitle>
                              I'm currently open to new opportunities. Whether you have a question, a project idea, or just want to say hi — let's connect!
                            </CTASubtitle>
                            <CTAButtonGroup>
                              <CTAPrimaryButton
                                href='/Urvansh_Shah_Resume.pdf'
                                target='_blank'
                                onClick={() => ReactGA.event({ category: 'CTA', action: 'resume_download', label: 'bottom_cta' })}
                              >
                                📄 Download Resume
                              </CTAPrimaryButton>
                              <CTASecondaryButton onClick={() => setContactModalOpen(true)}>
                                💬 Get In Touch
                              </CTASecondaryButton>
                            </CTAButtonGroup>
                          </CTABannerContainer>
                          <Testimonials />
                          <Footer
                            onContactClick={() => setContactModalOpen(true)}
                            onLocationClick={() => setLocationModalOpen(true)}
                          />
                          {openModal.state && (
                            <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />
                          )}
                          <ContactModal
                            isOpen={contactModalOpen}
                            onClose={() => setContactModalOpen(false)}
                          />
                          <LocationModal
                            isOpen={locationModalOpen}
                            onClose={() => setLocationModalOpen(false)}
                          />
                          <StickyDotNav />
                          <BackToTop />
                        </Body>
                        <FloatingContactButton />
                        <ChatWidget />
                      </>
                    } />
                  </Routes>
                </Router>
              )}
            </ThemeProvider>
  );
}

export default App;